import {
  Directive,
  ElementRef,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { TextNormalizerService } from '@shared/services/text-normalizer.service';

/**
 * Unicode Normalization Directive
 *
 * Automatically normalizes text input to NFC (Normalization Form Composed) for all
 * reactive form inputs (input[formControlName] and textarea[formControlName]).
 *
 * Why normalization is needed:
 * - Vietnamese IMEs (like UniKey) output characters in different normalization forms
 * - NFD (Normalization Form Decomposed): Characters are stored as base + combining marks
 *   Example: "ậ" = "â" (U+00E2) + combining dot below (U+0323) = two code points
 * - NFC (Normalization Form Composed): Characters are stored as single composed characters
 *   Example: "ậ" = single code point (U+1EAD)
 * - When NFD text is displayed, combining marks may render incorrectly (e.g., "Nhâ.p" instead of "Nhập")
 * - Regex validation can fail because NFD and NFC forms are different strings
 *
 * Why we use NFC:
 * - NFC is the standard form for storage and display
 * - It ensures consistent validation and rendering
 * - Most databases and APIs expect NFC form
 * - It prevents visual rendering issues with combining marks
 *
 * Why we check before calling setValue:
 * - Avoids unnecessary updates that can cause infinite valueChanges loops
 * - Prevents cursor position issues from unnecessary DOM updates
 * - Only updates when normalization actually changes the value
 *
 * Usage:
 * This directive is automatically applied to all inputs with formControlName.
 * No manual configuration needed - it works globally.
 *
 * @example
 * ```html
 * <input formControlName="categoryName" />
 * <textarea formControlName="description"></textarea>
 * ```
 */
@Directive({
  selector: 'input[formControlName], textarea[formControlName]',
  standalone: true,
})
export class UnicodeNormalizeDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef<HTMLInputElement | HTMLTextAreaElement>);
  private readonly ngControl = inject(NgControl, { optional: true });
  private readonly textNormalizer = inject(TextNormalizerService);

  // Track IME composition state to avoid normalizing during composition
  // Normalizing during composition breaks combining marks (e.g., "ậ" becomes "â.p")
  private isComposing = false;

  // Track if we're currently updating to prevent infinite loops
  private isUpdating = false;

  // Store bound event handlers for proper cleanup
  private boundHandleInput = this.handleInput.bind(this);
  private boundHandleCompositionStart = this.handleCompositionStart.bind(this);
  private boundHandleCompositionEnd = this.handleCompositionEnd.bind(this);
  private boundHandlePaste = this.handlePaste.bind(this);
  private boundHandleBlur = this.handleBlur.bind(this);

  ngOnInit(): void {
    const element = this.elementRef.nativeElement;

    // Only apply to text inputs and textareas (not number, email, etc. unless they're text type)
    // Check if element type is text, search, tel, url, password, or textarea
    if (
      element instanceof HTMLInputElement &&
      element.type !== 'text' &&
      element.type !== 'search' &&
      element.type !== 'tel' &&
      element.type !== 'url' &&
      element.type !== 'password' &&
      element.type !== ''
    ) {
      return; // Skip non-text inputs
    }

    // Listen to input events for real-time normalization
    element.addEventListener('input', this.boundHandleInput);

    // Listen to composition events to avoid normalizing during IME composition
    // compositionstart: User starts typing with IME (e.g., Vietnamese input)
    element.addEventListener('compositionstart', this.boundHandleCompositionStart);

    // compositionend: IME composition completes
    element.addEventListener('compositionend', this.boundHandleCompositionEnd);

    // Listen to paste events to normalize pasted text
    element.addEventListener('paste', this.boundHandlePaste);

    // Normalize on blur as a fallback to catch any missed cases
    element.addEventListener('blur', this.boundHandleBlur);
  }

  ngOnDestroy(): void {
    const element = this.elementRef.nativeElement;
    element.removeEventListener('input', this.boundHandleInput);
    element.removeEventListener('compositionstart', this.boundHandleCompositionStart);
    element.removeEventListener('compositionend', this.boundHandleCompositionEnd);
    element.removeEventListener('paste', this.boundHandlePaste);
    element.removeEventListener('blur', this.boundHandleBlur);
  }

  /**
   * Handle input events - normalize text in real-time
   * Skips normalization during IME composition to avoid breaking combining marks
   */
  private handleInput(event: Event): void {
    // Skip normalization during composition to avoid breaking combining marks
    if (this.isComposing || this.isUpdating) {
      return;
    }

    const element = event.target as HTMLInputElement | HTMLTextAreaElement;
    this.normalizeValue(element);
  }

  /**
   * Handle composition start - mark that IME composition has begun
   * During composition, we should NOT normalize to avoid breaking combining marks
   */
  private handleCompositionStart(): void {
    this.isComposing = true;
  }

  /**
   * Handle composition end - normalize after IME composition completes
   * This ensures proper NFC form for composed characters like "ậ", "ồ", etc.
   */
  private handleCompositionEnd(event: CompositionEvent): void {
    this.isComposing = false;

    // Normalize after composition ends to ensure proper NFC form
    // Use setTimeout to ensure composition is fully complete
    setTimeout(() => {
      const element = event.target as HTMLInputElement | HTMLTextAreaElement;
      this.normalizeValue(element, true);
    }, 0);
  }

  /**
   * Handle paste events - normalize pasted text
   * Pasted text may come from external sources in various normalization forms
   * We need to handle this more aggressively to catch NFD text
   */
  private handlePaste(event: ClipboardEvent): void {
    // Prevent default to intercept the paste
    // We'll handle it manually to ensure normalization
    event.preventDefault();
    
    // Get pasted text from clipboard
    const pastedText = event.clipboardData?.getData('text') || '';
    if (!pastedText) {
      return;
    }

    const element = event.target as HTMLInputElement | HTMLTextAreaElement;
    const start = element.selectionStart || 0;
    const end = element.selectionEnd || 0;
    const currentValue = element.value || '';

    // Normalize the pasted text immediately
    const normalizedPasted = this.textNormalizer.normalizeToNFC(pastedText, false);

    // Insert normalized text at cursor position
    const newValue =
      currentValue.substring(0, start) +
      normalizedPasted +
      currentValue.substring(end);

    // Normalize the entire value (in case there were other NFD characters)
    const normalizedValue = this.textNormalizer.normalizeToNFC(newValue, false);

    // Update the element value
    element.value = normalizedValue;

    // Update form control if available
    if (this.ngControl?.control && !this.isUpdating) {
      this.isUpdating = true;
      this.ngControl.control.setValue(normalizedValue, {
        emitEvent: false,
        onlySelf: true,
      });

      requestAnimationFrame(() => {
        // Set cursor position after the pasted text
        const newCursorPosition = Math.min(
          start + normalizedPasted.length,
          normalizedValue.length
        );
        element.setSelectionRange(newCursorPosition, newCursorPosition);

        const control = this.ngControl?.control;
        if (control) {
          control.markAsDirty();
          control.updateValueAndValidity({ emitEvent: true });
        }

        this.isUpdating = false;
      });
    } else {
      // If no control, just set cursor position
      const newCursorPosition = Math.min(
        start + normalizedPasted.length,
        normalizedValue.length
      );
      element.setSelectionRange(newCursorPosition, newCursorPosition);
    }
  }

  /**
   * Handle blur events - normalize on blur as a fallback
   * Catches any cases where normalization might have been missed
   */
  private handleBlur(event: FocusEvent): void {
    const element = event.target as HTMLInputElement | HTMLTextAreaElement;
    this.normalizeValue(element);
  }

  /**
   * Normalize the input value to NFC form
   *
   * @param element - The input or textarea element
   * @param preserveCursor - Whether to preserve cursor position (default: true)
   */
  private normalizeValue(
    element: HTMLInputElement | HTMLTextAreaElement,
    preserveCursor: boolean = true
  ): void {
    // Skip if no control or element is disabled
    if (!this.ngControl?.control || element.disabled || element.readOnly) {
      return;
    }

    // Skip if we're already updating to prevent infinite loops
    if (this.isUpdating) {
      return;
    }

    const currentValue = element.value || '';
    if (!currentValue) {
      return; // Nothing to normalize
    }

    // Normalize to NFC form using the text-normalizer service
    // Don't trim during typing to preserve trailing spaces
    const normalized = this.textNormalizer.normalizeToNFC(currentValue, false);

    // Check if normalization changed the value
    // We compare both string equality and normalization form to catch all cases
    const currentNormalized = currentValue.normalize('NFC');
    const needsUpdate = normalized !== currentValue || currentNormalized !== currentValue;

    if (needsUpdate) {
      this.isUpdating = true;

      // Preserve cursor position
      const cursorPosition = preserveCursor
        ? element.selectionStart || normalized.length
        : normalized.length;

      // Update form control value
      // Use emitEvent: false to prevent triggering valueChanges during normalization
      // This avoids infinite loops while still updating the control value
      this.ngControl.control.setValue(normalized, {
        emitEvent: false,
        onlySelf: true,
      });

      // Update the DOM element directly to ensure immediate visual update
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        element.value = normalized;

        // Restore cursor position
        if (preserveCursor) {
          const newCursorPosition = Math.min(cursorPosition, normalized.length);
          element.setSelectionRange(newCursorPosition, newCursorPosition);
        }

        // Mark control as dirty and touched to maintain form state
        const control = this.ngControl?.control;
        if (control) {
          control.markAsDirty();
          control.updateValueAndValidity({ emitEvent: true });
        }

        this.isUpdating = false;
      });
    }
  }
}
